import { useCallback } from 'react';
import { trackEvent } from '../utils/analyticsService';
import { outlineToMainTabWorkspacePatch, scheduleInterleavedOutlineInsert } from '../utils/applySmartLessonOutline';
import { sectionToEditorHtml } from '../utils/lessonOutlineApply';

// Lowercase exerciseType (the string GeneratorPanelsContainer passes to
// onInsertToBoard) → canonical Tiptap node name. Without this map, only
// 'abcd' was being wrapped as `{ type: 'abcdExercise', attrs: ... }`;
// every other exercise type was inserted as raw JSON, which Tiptap
// silently dropped — hence the "ABCD works but Word Translation doesn't"
// report. Kept lowercase-keyed because the generator panels normalize
// their type strings (e.g. 'wordtranslation' not 'wordTranslation').
const EXERCISE_TYPE_TO_NODE = {
  abcd: 'abcdExercise',
  openquestions: 'openQuestionsExercise',
  quiz: 'quizNode',
  worddefinition: 'wordDefinitionExercise',
  wordtranslation: 'wordTranslationExercise',
  fillinthegaps: 'fillInTheGapsExercise',
  truefalse: 'trueFalseExercise',
  warmupdiscussion: 'warmUpDiscussionExercise',
  createdialogue: 'createDialogueExercise',
  essentialvocabulary: 'essentialVocabularyExercise',
  leadinactivities: 'leadInActivitiesExercise',
  fouropinions: 'fourOpinionsExercise',
  communicativesituations: 'communicativeSituations',
  proscons: 'prosConsExercise',
  matchinghalves: 'matchingHalves',
  errorcorrection: 'errorCorrection',
  grammarrules: 'grammarRulesExercise',
  jumbledsentence: 'jumbledSentence',
  multiplechoice: 'multipleChoice',
};

export function useExerciseBoardInsertion({
  setContainers, setEditorContents, setWhiteboards, setHomeworkContainers,
  allEditorsRef, whiteboardsRef,
  currentTab, whiteboards, activeEditorId, allEditors, mainEditor,
  whiteboardCreators, setToast,
}) {
  // Функция для получения активного редактора
  const getActiveEditor = () => {
    return allEditors.get(activeEditorId) || mainEditor;
  };

  // Smart Lesson: replace main-tab containers with one per section, then insert content.
  // Sections with blockType === 'homework' are routed to the homework tab automatically.
  const handleInsertSmartLesson = useCallback((outline, applyMeta) => {
    const allSections = Array.isArray(outline?.sections) ? outline.sections : [];
    const mainSections = allSections.filter(s => s.blockType !== 'homework');
    const hwSections   = allSections.filter(s => s.blockType === 'homework');
    const lessonTitle  = outline?.lessonTitle || '';

    // ── Main tab ──────────────────────────────────────────────────────────────
    const mainOutline = { ...outline, sections: mainSections };
    const { containers: newContainers, editorContentsPatch } = outlineToMainTabWorkspacePatch(mainOutline);
    setContainers(newContainers);
    setEditorContents(prev => ({ ...prev, ...editorContentsPatch }));
    scheduleInterleavedOutlineInsert(
      mainOutline,
      (id) => allEditorsRef.current.get(id),
      newContainers.map(c => c.id),
      applyMeta,
    );

    // ── Homework tab ──────────────────────────────────────────────────────────
    // We pre-populate editorContents directly (no editor mounting required),
    // so the content is ready as initialContent when the user opens the tab.
    if (hwSections.length === 0) return;

    const base = Date.now() + 1000;
    let targetHomeworkId;
    const existingHwTab = whiteboardsRef.current?.find(wb => wb.type === 'homework');
    if (existingHwTab) {
      targetHomeworkId = existingHwTab.id;
    } else {
      targetHomeworkId = base;
      setWhiteboards(prev => [...prev, { id: targetHomeworkId, type: 'homework', title: 'Homework' }]);
      setHomeworkContainers(prev => ({ ...prev, [targetHomeworkId]: [] }));
    }

    const hwContainerIds = hwSections.map((_, i) => base + i + 1);

    // Build editorContents patch for homework containers before the tab is opened
    const hwEditorPatch = {};
    hwSections.forEach((sec, i) => {
      const key = `homework-${targetHomeworkId}-container-${hwContainerIds[i]}`;
      hwEditorPatch[key] = sectionToEditorHtml(sec, lessonTitle);
    });

    setHomeworkContainers(prev => ({
      ...prev,
      [targetHomeworkId]: [
        ...(prev[targetHomeworkId] || []),
        ...hwContainerIds.map(id => ({ id, type: 'editor' })),
      ],
    }));
    setEditorContents(prev => ({ ...prev, ...hwEditorPatch }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setContainers, setEditorContents, setWhiteboards, setHomeworkContainers, allEditorsRef, whiteboardsRef]);

  // Функция для вставки упражнений на доску
  // Lowercase exerciseType (the string GeneratorPanelsContainer passes to
  // onInsertToBoard) → canonical Tiptap node name. Without this map, only
  // 'abcd' was being wrapped as `{ type: 'abcdExercise', attrs: ... }`;
  // every other exercise type was inserted as raw JSON, which Tiptap
  // silently dropped — hence the "ABCD works but Word Translation doesn't"
  // report. Kept lowercase-keyed because the generator panels normalize
  // their type strings (e.g. 'wordtranslation' not 'wordTranslation').
  const insertExerciseToBoard = (exerciseData, exerciseType) => {
    console.log('🔥 insertExerciseToBoard called:', { exerciseData, exerciseType, currentTab });

    // Best-effort: fires on invocation rather than confirmed success (the
    // whiteboard branch below has async/best-effort creators of its own),
    // but every generator panel only calls this once its own AI generation
    // has already succeeded, so this is a reliable proxy for "an exercise
    // was generated." 'grid'/'material' are board utilities, not exercises.
    if (exerciseType !== 'grid' && exerciseType !== 'material') {
      trackEvent('exercise_generated', { exerciseType: String(exerciseType || '').toLowerCase() });
    }

    // Doc and homework tabs are editor-based (not whiteboards), so they
    // need the same activeEditor path as the main lesson editor.
    const currentWb = currentTab > 0 ? whiteboards[currentTab - 1] : null;
    const isEditorTab = currentTab === 0 || currentWb?.type === 'doc' || currentWb?.type === 'homework';

    if (isEditorTab) {
      // Pick the target editor:
      //  - On the main lesson tab, fall back to the active editor /
      //    main editor as before.
      //  - On a homework or doc tab, prefer an editor inside that tab.
      //    Otherwise getActiveEditor() returns the lesson's main editor
      //    (its default fallback) and the exercise lands in the wrong
      //    place — silently from the teacher's point of view.
      let targetEditor = null;
      if (currentWb && (currentWb.type === 'homework' || currentWb.type === 'doc')) {
        const tabPrefix = `${currentWb.type === 'homework' ? 'homework' : 'doc'}-${currentWb.id}-`;
        // Prefer the active editor if it lives inside this tab.
        if (activeEditorId && String(activeEditorId).startsWith(tabPrefix)) {
          targetEditor = allEditors.get(activeEditorId);
        }
        // Otherwise grab the LAST registered editor for this tab — that's
        // where new content typically goes (append to the bottom).
        if (!targetEditor && allEditorsRef.current) {
          const matching = Array.from(allEditorsRef.current.keys())
            .filter(id => String(id).startsWith(tabPrefix));
          if (matching.length > 0) targetEditor = allEditorsRef.current.get(matching[matching.length - 1]);
        }
      }
      if (!targetEditor) targetEditor = getActiveEditor();

      if (targetEditor && targetEditor.chain) {
        // Build the Tiptap content from `exerciseData` + the lowercased
        // type. Anything not in the map is passed through unchanged so
        // generator panels that already produce a `{ type, attrs }` node
        // (or plain text/HTML) keep working.
        const lowerType = String(exerciseType || '').toLowerCase();
        let content;
        if (lowerType === 'text') {
          content = exerciseData;
        } else if (EXERCISE_TYPE_TO_NODE[lowerType]) {
          content = { type: EXERCISE_TYPE_TO_NODE[lowerType], attrs: exerciseData };
        } else {
          content = exerciseData;
        }
        targetEditor.chain().focus().insertContent(content).run();
        return;
      }
    } else {
      // Если мы на доске, создаем элемент упражнения или материала
      const whiteboardId = whiteboards[currentTab - 1]?.id;
      console.log('🔥 Whiteboard ID:', whiteboardId);

      if (whiteboardId) {
        // Получаем функции создания элементов для текущей доски
        const creators = whiteboardCreators[currentTab];

        if (creators) {
          if (exerciseType === 'grid' && creators.createGridElement) {
            creators.createGridElement(exerciseData.rows, exerciseData.cols);
          } else if (exerciseType === 'material' && creators.createMaterialElement) {
            // Создаем элемент материала на доске в центре viewport
            const result = creators.createMaterialElement(exerciseData, undefined, undefined, creators.setPdfModal);
            if (result) {
              setToast(`Material "${exerciseData.name}" added to board`);
              setTimeout(() => setToast(null), 3000);
            }
            // Если result === null, значит открылось PDF модальное окно
          } else if (creators.createExerciseElement) {
            // Создаем элемент упражнения в центре текущего viewport
            creators.createExerciseElement(exerciseData, exerciseType);
            setToast(`Exercise added to board`);
            setTimeout(() => setToast(null), 3000);
          } else {
            console.log('🔥 No suitable creator found');
            setToast('Unable to insert to board. Please try again.');
            setTimeout(() => setToast(null), 3000);
          }
        } else {
          console.log('🔥 No creators found');
          setToast('Unable to insert to board. Please try again.');
          setTimeout(() => setToast(null), 3000);
        }
      }
    }
  };

  // Функция для форматирования упражнения для доски
  // eslint-disable-next-line no-unused-vars
  const formatExerciseForBoard = (exerciseData, exerciseType) => {
    switch (exerciseType) {
      case 'abcd':
        return formatABCDExerciseForBoard(exerciseData);
      case 'text':
        return exerciseData;
      case 'quiz':
        return formatQuizExerciseForBoard(exerciseData);
      default:
        return exerciseData;
    }
  };

  const formatABCDExerciseForBoard = (exerciseData) => {
    let text = `${exerciseData.title || 'ABCD Exercise'}\n\n`;
    exerciseData.questions.forEach((question, index) => {
      text += `${index + 1}. ${question.question}\n`;
      question.options.forEach(option => {
        text += `   ${option.id}) ${option.text}\n`;
      });
      text += '\n';
    });
    return text;
  };

  const formatQuizExerciseForBoard = (exerciseData) => {
    let text = `${exerciseData.title || 'Quiz Exercise'}\n\n`;
    exerciseData.questions.forEach((question, index) => {
      text += `${index + 1}. ${question.question}\n`;
      if (question.options) {
        question.options.forEach(option => {
          text += `   ${option.id}) ${option.text}\n`;
        });
      }
      text += '\n';
    });
    return text;
  };

  return { handleInsertSmartLesson, insertExerciseToBoard };
}
